import { Timeseries } from "../types.ts";

export interface KalmanOptions {
  Q: number; // process noise variance
  R: number; // measurement noise variance
  x0?: number; // initial state estimate (default: first observation)
  P0?: number; // initial covariance (default: R)
}

export interface KalmanResult {
  filtered: (number | null)[];
  timestamps: Date[];
}

/**
 * Univariate (scalar) Kalman filter.
 *
 * State model:   x(t) = x(t-1) + w,  w ~ N(0, Q)
 * Observation:   z(t) = x(t) + v,     v ~ N(0, R)
 *
 * Null observations trigger a predict-only step — the state estimate
 * carries forward unchanged while covariance grows, so the filter
 * naturally becomes less certain during gaps.
 */
export function computeKalman(ts: Timeseries, options: KalmanOptions): KalmanResult {
  const { Q, R } = options;
  const { data, timestamps } = ts;

  if (data.length === 0) {
    return { filtered: [], timestamps: [] };
  }

  // Find first non-null observation for initialisation
  let firstIdx = 0;
  while (firstIdx < data.length && data[firstIdx] == null) firstIdx++;
  if (firstIdx >= data.length) {
    return { filtered: data.map(() => null), timestamps };
  }

  const x0 = options.x0 ?? data[firstIdx]!;
  const P0 = options.P0 ?? R;

  const filtered: (number | null)[] = new Array(data.length).fill(null);
  let x = x0;
  let P = P0;

  for (let i = firstIdx; i < data.length; i++) {
    // --- Predict ---
    const xPred = x;
    const PPred = P + Q;

    // --- Update ---
    const z = data[i];
    if (z == null) {
      // No observation — carry prediction forward
      x = xPred;
      P = PPred;
      filtered[i] = null;
    } else {
      const K = PPred / (PPred + R); // Kalman gain
      x = xPred + K * (z - xPred);
      P = (1 - K) * PPred;
      filtered[i] = x;
    }
  }

  return { filtered, timestamps };
}
